// FinancingRequestPDF.jsx
import { Document, Image, StyleSheet, Text, View } from "@react-pdf/renderer";
import { isNil } from "lodash";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import PdfPageBranded from "./components/PdfPageBranded";

// ─── Color Palette ───
const COLORS = {
  primary: "#1a3a5c",
  primaryLight: "#e8eef4",
  accent: "#2c6fbb",
  border: "#d0d7de",
  borderLight: "#e8ecf0",
  headerBg: "#f0f4f8",
  cellAlt: "#f8fafc",
  text: "#1a1a1a",
  textSecondary: "#222222",
  //   textSecondary: "#555555",
  textMuted: "#888888",
  white: "#ffffff",
  labelBg: "#f5f7f9",
};

// ─── Styles ───
const s = StyleSheet.create({
  page: { paddingBottom: 60 },

  // Section
  sectionContainer: { marginBottom: 24 },
  sectionHeader: {
    backgroundColor: COLORS.primary,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: 9,
    // fontSize: 10,
    // fontSize: 11,
    fontWeight: "bold",
    color: COLORS.white,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sectionBody: {
    border: `1px solid ${COLORS.border}`,
    borderTopWidth: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    overflow: "hidden",
  },

  // Field rows
  fieldRow: {
    flexDirection: "row",
    borderBottom: `1px solid ${COLORS.borderLight}`,
    minHeight: 22,
  },
  fieldRowLast: {
    borderBottomWidth: 0,
  },
  fieldLabel: {
    width: "40%",
    backgroundColor: COLORS.labelBg,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRight: `1px solid ${COLORS.borderLight}`,
    justifyContent: "center",
  },
  fieldLabelText: {
    fontSize: 7.5,
    fontWeight: 500,
    // fontWeight: "bold",
    color: COLORS.textSecondary,
  },
  fieldValue: {
    width: "60%",
    paddingVertical: 5,
    paddingHorizontal: 8,
    justifyContent: "center",
  },
  fieldValueText: {
    fontSize: 8,
    color: COLORS.text,
  },
  emptyValue: {
    fontSize: 8,
    color: COLORS.textMuted,
    fontStyle: "italic",
  },

  // Two-column layout
  twoColRow: {
    flexDirection: "row",
    borderBottom: `1px solid ${COLORS.borderLight}`,
    minHeight: 22,
  },
  twoColCell: {
    flex: 1,
    flexDirection: "row",
  },
  twoColDivider: {
    width: 1,
    backgroundColor: COLORS.borderLight,
  },

  // Sub-section header (inside a section)
  subHeader: {
    backgroundColor: "#ffffff",
    // backgroundColor: COLORS.headerBg,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottom: `2px solid ${COLORS.border}`,
  },
  subHeaderText: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.primary,
  },

  // Signature
  signatureBlock: {
    marginTop: 6,
    borderTop: `1px solid ${COLORS.border}`,
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signatureImage: {
    width: 180,
    height: 60,
    objectFit: "contain",
    border: `1px solid ${COLORS.borderLight}`,
    borderRadius: 2,
  },
  signatureLine: {
    width: 180,
    height: 60,
    borderBottom: `1px solid ${COLORS.text}`,
  },
  signatureLabel: {
    fontSize: 7,
    color: COLORS.textMuted,
    marginTop: 3,
  },

  // Authorization
  authText: {
    fontSize: 6.5,
    color: COLORS.textSecondary,
    lineHeight: 1.5,
    marginBottom: 4,
  },
  authTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.primary,
    marginBottom: 4,
  },

  // Borrower card header
  borrowerBadge: {
    backgroundColor: COLORS.primaryLight,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  borrowerBadgeText: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.primary,
  },
});

// ──────────────────────────────────────────────
// REUSABLE PRIMITIVES
// ──────────────────────────────────────────────

/** Section wrapper with colored header */
const PdfFormSection = ({ title, children }) => (
  <View style={s.sectionContainer}>
    <View style={s.sectionHeader}>
      <Text style={s.sectionTitle}>{title}</Text>
    </View>
    <View style={s.sectionBody}>{children}</View>
  </View>
);

/** Sub-header row inside a section */
const PdfSubHeader = ({ title }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

/** Single field row: label | value */
const PdfField = ({ label, value, isLast = false }) => {
  const displayValue = formatFieldValue(value);
  return (
    <View style={[s.fieldRow, isLast && s.fieldRowLast]}>
      <View style={s.fieldLabel}>
        <Text style={s.fieldLabelText}>{label}</Text>
      </View>
      <View style={s.fieldValue}>
        {isNil(value) || value === "" ? (
          <Text style={s.emptyValue}>—</Text>
        ) : (
          <Text style={s.fieldValueText}>{displayValue}</Text>
        )}
      </View>
    </View>
  );
};

/** Two fields side by side in one row */
const PdfFieldPair = ({ left, right, isLast = false }) => (
  <View style={[s.twoColRow, isLast && s.fieldRowLast]}>
    <View style={s.twoColCell}>
      <View style={[s.fieldLabel, { width: "45%" }]}>
        <Text style={s.fieldLabelText}>{left.label}</Text>
      </View>
      <View style={[s.fieldValue, { width: "55%" }]}>
        {isNil(left.value) || left.value === "" ? (
          <Text style={s.emptyValue}>—</Text>
        ) : (
          <Text style={s.fieldValueText}>{formatFieldValue(left.value)}</Text>
        )}
      </View>
    </View>
    <View style={s.twoColDivider} />
    <View style={s.twoColCell}>
      <View style={[s.fieldLabel, { width: "45%" }]}>
        <Text style={s.fieldLabelText}>{right.label}</Text>
      </View>
      <View style={[s.fieldValue, { width: "55%" }]}>
        {isNil(right.value) || right.value === "" ? (
          <Text style={s.emptyValue}>—</Text>
        ) : (
          <Text style={s.fieldValueText}>{formatFieldValue(right.value)}</Text>
        )}
      </View>
    </View>
  </View>
);

// ──────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────

const formatFieldValue = (value) => {
  if (isNil(value)) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "object" && value?.fullAddress) return value.fullAddress;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDollarPdf = (value) => {
  if (isNil(value) || value === "") return null;
  const num =
    typeof value === "string"
      ? parseFloat(value.replace(/[^0-9.-]/g, ""))
      : value;
  if (isNaN(num)) return value;
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentPdf = (value) => {
  if (isNil(value) || value === "") return null;
  return `${value}%`;
};

// ──────────────────────────────────────────────
// SECTION COMPONENTS
// ──────────────────────────────────────────────

const LoanDetailsSection = ({ data }) => (
  <PdfFormSection title="Loan Details">
    <PdfFieldPair
      left={{ label: "Loan Product Type", value: data?.loanProductType }}
      right={{ label: "Loan Purpose", value: data?.loanPurpose }}
    />
    <PdfField
      label="Requested Loan Amount / Initial Loan Amount"
      value={formatDollarPdf(data?.baseLoanAmount)}
    />
    <PdfField label="Exit Strategy" value={data?.exitStrategy} />

    {data?.loanProductType === "DSCR" && (
      <>
        <PdfField
          label="The DSCR for the loan as measured by counterparty"
          value={data?.dscr}
        />
        <PdfFieldPair
          left={{
            label: "Total In Place Rents",
            value: formatDollarPdf(data?.totalInPlaceRents),
          }}
          right={{
            label: "Total Market Rents",
            value: formatDollarPdf(data?.totalMarketRents),
          }}
        />
      </>
    )}

    {/* Refinance Details sub-section */}
    {isNil(data?.loanPurpose) ? null : data?.loanPurpose ===
        "Refinance - No Cash Out" ||
      data?.loanPurpose === "Refinance - Cash Out" ? (
      <>
        <PdfSubHeader title="Refinance Details" />
        <PdfField
          label="Outstanding Loan UPB (Expected Payoff)"
          value={formatDollarPdf(data?.outstandingLoanUpb)}
        />
        <PdfField label="Current Lender" value={data?.exisitingLender} />
        <PdfField
          label="Existing Loan Maturity Date"
          value={data?.existingLoanMaturityDate}
        />
        <PdfFieldPair
          left={{
            label: "Late Payments (30+ Days)",
            value: data?.existingLoanLatePayments,
          }}
          right={{
            label: "Loan In Default",
            value: data?.existingLoanInDefault,
          }}
        />
        <PdfField label="Default Type" value={data?.existingLoanDefaultType} />
        <PdfFieldPair
          left={{
            label: "Has Undrawn Budget",
            value: data?.existingLoanHasUndrawnBudget,
          }}
          right={{
            label: "Undrawn Budget Amount",
            value: formatDollarPdf(data?.existingLoanUndrawnBudgetAmount),
          }}
        />
        <PdfFieldPair
          left={{
            label: "Property Listed for Sale",
            value: data?.propertyListedForSale,
          }}
          right={{
            label: "Listed in Last 6 Months",
            value: data?.propertyListedForSaleLastSixMonths,
          }}
          isLast
        />
      </>
    ) : null}
  </PdfFormSection>
);

const PropertySection = ({ data }) => {
  const properties = data?.subjectProperties || [{}];
  return (
    <PdfFormSection title="Property Details">
      {properties.map((prop, idx) => (
        <View key={idx}>
          {properties.length > 1 && (
            <PdfSubHeader title={`Property ${idx + 1}`} />
          )}
          <PdfField label="Property Address" value={prop?.address} />
          <PdfFieldPair
            left={{ label: "Property Type", value: prop?.propertyType }}
            right={{
              label: "Proposed Property Type",
              value: prop?.proposedPropertyType,
            }}
          />
          <PdfField label="Flood Zone" value={prop?.floodZone} />

          {/* Purchase fields */}
          <PdfSubHeader title="Purchase Details" />
          <PdfFieldPair
            left={{
              label: "Purchase Price",
              value: formatDollarPdf(prop?.purchasePrice),
            }}
            right={{ label: "Purchase Date", value: prop?.purchaseDate }}
          />
          <PdfFieldPair
            left={{
              label: "Construction Budget",
              value: formatDollarPdf(prop?.constructionBudget),
            }}
            right={{
              label: "After Renovation Value",
              value: formatDollarPdf(prop?.arv),
            }}
          />
          <PdfFieldPair
            left={{
              label: "Original Purchase Price",
              value: formatDollarPdf(prop?.originalPurchasePrice),
            }}
            right={{
              label: "Original Purchase Date",
              value: prop?.originalPurchaseDate,
            }}
          />

          {/* Condition */}
          <PdfSubHeader title="Property Condition" />
          <PdfField
            label="Property Listed for Sale"
            value={prop?.propertyListedForSale}
          />
          <PdfFieldPair
            left={{
              label: "Last Listed Date",
              value: prop?.lastListedForSaleDate,
            }}
            right={{
              label: "Last Listed Price",
              value: formatDollarPdf(prop?.lastListedForSalePrice),
            }}
          />
          <PdfField label="Fix & Flip Status" value={prop?.ffPropertyStatus} />
          <PdfField
            label="Ground Up Construction Status"
            value={prop?.gucPropertyStatus}
            isLast={idx === properties.length - 1}
          />
        </View>
      ))}
    </PdfFormSection>
  );
};

const BorrowersSection = ({ data }) => {
  const borrowers = data?.borrowers || [{}];
  return (
    <PdfFormSection title="Borrower Information">
      {borrowers.map((b, idx) => (
        <View key={idx}>
          <View style={s.borrowerBadge}>
            <Text style={s.borrowerBadgeText}>
              {idx === 0 ? "Primary Borrower" : `Co-Borrower ${idx}`}
            </Text>
          </View>
          <PdfFieldPair
            left={{ label: "First Name", value: b?.firstName }}
            right={{ label: "Last Name", value: b?.lastName }}
          />
          <PdfFieldPair
            left={{ label: "Email", value: b?.emailAddress }}
            right={{ label: "Phone", value: formatPhone(b?.phone) }}
          />
          <PdfFieldPair
            left={{ label: "Date of Birth", value: formatDate(b?.dateOfBirth) }}
            right={{ label: "Citizenship Status", value: b?.citizenshipStatus }}
          />
          <PdfField label="Primary Address" value={b?.primaryAddress} />
          <PdfField label="Non-US Address" value={b?.nonUSborrowerAddress} />
          <PdfFieldPair
            left={{ label: "Marital Status", value: b?.maritalStatus }}
            right={{ label: "Has SSN", value: b?.borrowerHasSSN }}
          />
          <PdfField
            label="SSN"
            value={
              b?.borrowerSSN ? "••••••" + String(b.borrowerSSN).slice(-4) : null
            }
            isLast={idx === borrowers.length - 1}
          />
        </View>
      ))}
    </PdfFormSection>
  );
};

const BorrowingEntitySection = ({ data }) => {
  const entity = data?.borrowerEntity || {};
  return (
    <PdfFormSection title="Borrowing Entity">
      <PdfFieldPair
        left={{ label: "Entity Name", value: entity?.entityName }}
        right={{ label: "Entity Type", value: entity?.entityType }}
      />
      <PdfFieldPair
        left={{
          label: "State of Organization",
          value: entity?.entityStateOfOrganization,
        }}
        right={{
          label: "Organization Date",
          value: entity?.entityOrganizationDate,
        }}
      />
      <PdfField
        label="Borrower Ownership %"
        value={formatPercentPdf(entity?.applicantEntityPercentOwnership)}
        isLast
      />
    </PdfFormSection>
  );
};

const AuthorizationSection = ({ data }) => (
  <PdfFormSection title="Authorization & Signature">
    <View style={{ padding: 10 }}>
      <Text style={s.authTitle}>Authorization Agreement</Text>
      <Text style={s.authText}>
        Applicant attests that this application is for financing of a business
        purpose loan and any subject properties covered under this application
        are not owner occupied, used as a second home or vacation property, and
        any recent use of the property as a primary residence will require
        supporting evidence that all guarantors and their family members are no
        longer occupying the residence.
      </Text>
      <Text style={s.authText}>
        The actual terms and conditions upon which the Lender might extend
        credit are subject to satisfactory completion of due diligence, internal
        approval, satisfactory review of documentation and such other or
        different items and conditions as may be determined by the Lender
        following the completion of its underwriting. Lender's approval of the
        loan documents shall not constitute a final approval of the loan and the
        loan documents shall not be considered final until the Underwriting and
        Approval Process is completed.
      </Text>
      <Text style={s.authText}>
        The borrower hereby agrees that all aforementioned information is true
        and in no means is false and intended to influence the credit
        underwriting of the Lender. The borrower understands that this loan
        application does not constitute a commitment from the Lender. All fees
        collected in relation to loan due diligence are non-refundable.
      </Text>

      <View style={s.signatureBlock}>
        <View>
          {data?.signature ? (
            <Image src={data.signature} style={s.signatureImage} />
          ) : (
            <View style={s.signatureLine} />
          )}
          <Text style={s.signatureLabel}>Borrower Signature</Text>
        </View>
        <View style={{ alignItems: "flex-end" }}>
          <Text style={{ fontSize: 8, color: COLORS.text, marginBottom: 2 }}>
            {formatDate(new Date())}
          </Text>
          <Text style={s.signatureLabel}>Date</Text>
        </View>
      </View>
    </View>
  </PdfFormSection>
);

// ──────────────────────────────────────────────
// MAIN DOCUMENT
// ──────────────────────────────────────────────

const FinancingRequestPDF = ({ formData = {} }) => {
  const today = new Date().toISOString().split("T")[0];
  return (
    <Document>
      <PdfPageBranded
        pageNumber={1}
        title="Loan Application"
        logo
        titleDescription={`Submitted by ${formData?.borrowers[0]?.firstName} ${formData?.borrowers[0]?.lastName} on ${formatDate(today)}`}
        footerDate={today}
      >
        <LoanDetailsSection data={formData} />
        <PropertySection data={formData} />
      </PdfPageBranded>

      <PdfPageBranded
        title="Loan Application"
        logo
        titleDescription={`Submitted by ${formData?.borrowers[0]?.firstName} ${formData?.borrowers[0]?.lastName} on ${formatDate(today)}`}
        footerDate={today}
        pageNumber={2}
      >
        <BorrowersSection data={formData} />
        <BorrowingEntitySection data={formData} />
        <AuthorizationSection data={formData} />
      </PdfPageBranded>
    </Document>
  );
};

export default FinancingRequestPDF;
